/**
 * @fileoverview Reusable, searchable guide view. Renders a categorized,
 * filterable knowledge base from a `categories` data array. Shared by the
 * instructor guide (GuidePage) and the student/parent guide (StudentGuidePage)
 * so both stay visually identical while carrying audience-specific content.
 *
 * Layout notes: the page runs to a 1680px measure rather than Tailwind's
 * `max-w-7xl` (1280px). At 1440px+ the old cap stranded ~320px of empty gutter
 * on each side and left the topic rail floating in the middle of the page
 * instead of anchored near the edge. Reading comfort is protected per-block
 * (prose is capped in `ch`, item lists go multi-column) rather than by
 * squeezing the whole page.
 *
 * Spacing uses flex + `gap`, never `space-y-*`: this project's Tailwind v4
 * setup emits no `space-y-*` utilities, so those classes are silently inert.
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, HelpCircle, ChevronDown, Mail, PlayCircle } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { supportEmail } from "@/lib/companyBrand";
import { categoriesForClient, clientFromSearch } from "@/lib/guideClient";

/* ------------------------------------------------------------------ */
/*  Helper: flatten all searchable text from a section                 */
/* ------------------------------------------------------------------ */

const sectionText = (section) => {
  const parts = [section.title, section.content || ""];
  if (section.steps) parts.push(section.steps.join(" "));
  if (section.items) parts.push(section.items.map((i) => `${i.label} ${i.desc}`).join(" "));
  if (section.note) parts.push(section.note);
  if (section.media?.caption) parts.push(section.media.caption);
  return parts.join(" ").toLowerCase();
};

/* ------------------------------------------------------------------ */
/*  Media                                                              */
/* ------------------------------------------------------------------ */

/**
 * A screenshot or walkthrough GIF of the real app.
 *
 * Every asset in `public/academy` is captured from the shipped instructor UI
 * by `scripts/capture-academy.mjs`, so these are photographs of the product
 * rather than illustrations of it. Images are lazy so a guide carrying a dozen
 * of them still paints immediately.
 */
const MediaFigure = ({ media, className = "" }) => (
  <figure
    className={`overflow-hidden rounded-2xl border border-[var(--studio-divider-strong)] bg-[var(--studio-base)] ${className}`}
  >
    <img
      src={media.src}
      alt={media.alt}
      loading="lazy"
      decoding="async"
      className="block w-full"
      data-testid="guide-media"
    />
    {media.caption && (
      <figcaption className="flex items-center gap-2 border-t border-[var(--studio-divider)] px-4 py-2.5">
        {media.kind === "gif" && (
          <PlayCircle className="h-3.5 w-3.5 shrink-0 text-[var(--accent-yellow-text)]" strokeWidth={1.7} />
        )}
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--text-tertiary)]">
          {media.caption}
        </span>
      </figcaption>
    )}
  </figure>
);

/* ------------------------------------------------------------------ */
/*  Section renderers                                                  */
/* ------------------------------------------------------------------ */

const SectionBody = ({ section }) => (
  <div className="flex flex-col gap-4">
    {section.content && (
      <p className="max-w-[68ch] text-sm leading-relaxed text-[var(--text-secondary)]">
        {section.content}
      </p>
    )}

    {section.steps && (
      <ol className="flex max-w-[70ch] flex-col gap-2.5 pl-0">
        {section.steps.map((step, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-[var(--text-secondary)]">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--accent-yellow-dim)] font-mono text-[11px] font-medium text-[var(--accent-yellow-text)]">
              {i + 1}
            </span>
            <span className="leading-relaxed">{step}</span>
          </li>
        ))}
      </ol>
    )}

    {section.items && (
      <div
        className={`grid gap-2 ${
          // A long definition list reads better in two columns than as one tall
          // ribbon down an otherwise empty card.
          section.items.length > 3 && !section.media ? "sm:grid-cols-2" : ""
        }`}
      >
        {section.items.map((item, i) => (
          <div
            key={i}
            className="flex items-start gap-3 rounded-xl bg-[var(--studio-overlay)]/60 px-4 py-3"
          >
            <div className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent-yellow)]/60" />
            <div className="min-w-0">
              <span className="text-sm font-medium text-[var(--text-primary)]">{item.label}</span>
              <span className="text-sm text-[var(--text-tertiary)]"> — {item.desc}</span>
            </div>
          </div>
        ))}
      </div>
    )}

    {section.note && (
      <div className="max-w-[72ch] rounded-xl border border-[var(--accent-yellow)]/10 bg-[var(--accent-yellow)]/5 px-4 py-3 text-sm text-[var(--text-secondary)]">
        <span className="font-medium text-[var(--accent-yellow-text)]">Note: </span>
        {section.note}
      </div>
    )}
  </div>
);

/**
 * One how-to. With media attached the card becomes two columns on large
 * screens — instructions beside the screenshot — which is what actually fills
 * the width without stretching prose into unreadable 200-character lines.
 * `layout: "wide"` instead puts the media full-width beneath the text, for the
 * flagship walkthroughs where detail matters more than adjacency.
 */
const SectionCard = ({ section }) => {
  const media = section.media;
  const sideBySide = media && media.layout !== "wide";

  return (
    <div
      id={section.id}
      className={`scroll-mt-8 rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)]/50 p-5 md:p-6 ${
        // A card carrying a screenshot needs the full measure for its two
        // columns; text-only cards pair up instead of each leaving half the
        // row empty.
        media ? "xl:col-span-2" : ""
      }`}
    >
      <h3 className="mb-3 text-base font-medium text-[var(--text-primary)]">{section.title}</h3>

      {sideBySide ? (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,46%)] lg:gap-8">
          <SectionBody section={section} />
          <MediaFigure media={media} className="self-start" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <SectionBody section={section} />
          {/* Capped: at the full card measure a walkthrough GIF is ~1240px
              wide and swamps the instructions it is meant to illustrate. */}
          {media && <MediaFigure media={media} className="w-full max-w-[760px]" />}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const GuideView = ({
  categories: allCategories,
  eyebrow = "Knowledge Base",
  title = "Hans Academy",
  intro = "Everything you need to get the most out of Hans. Search below or browse by topic.",
}) => {
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(null); // null = show all
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const location = useLocation();
  // Opened from the iOS app (?client=ios): no web purchase copy, and
  // PublicLayout gives the page the app's navbar and footer. See
  // lib/guideClient for how sections opt in.
  const client = clientFromSearch(location.search);
  const categories = useMemo(() => categoriesForClient(allCategories, client), [allCategories, client]);
  const contentRef = useRef(null);
  const hasInteracted = useRef(false);

  // Deep-link: select category from hash on load
  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.slice(1);
      const match = categories.find(
        (cat) => cat.id === hash || cat.sections.some((s) => s.id === hash)
      );
      if (match) setActiveId(match.id);
    }
  }, [location.hash, categories]);

  // Scroll content area to top when switching tabs (skip initial load)
  useEffect(() => {
    if (!hasInteracted.current) return;
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeId]);

  // Filter categories by search query AND active tab
  const filtered = useMemo(() => {
    let cats = categories;

    // Tab filter
    if (activeId) {
      cats = cats.filter((cat) => cat.id === activeId);
    }

    // Search filter
    if (query.trim()) {
      const q = query.toLowerCase();
      cats = cats
        .map((cat) => {
          const matchingSections = cat.sections.filter(
            (s) => s.title.toLowerCase().includes(q) || sectionText(s).includes(q)
          );
          if (matchingSections.length > 0 || cat.title.toLowerCase().includes(q)) {
            return { ...cat, sections: matchingSections.length > 0 ? matchingSections : cat.sections };
          }
          return null;
        })
        .filter(Boolean);
    }

    return cats;
  }, [query, activeId, categories]);

  const selectCategory = (id) => {
    hasInteracted.current = true;
    setActiveId(id === activeId ? null : id);
    setMobileTocOpen(false);
  };

  const tocButtonClass = (isActive) =>
    `flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm transition-colors ${
      isActive
        ? "bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
        : "text-[var(--text-secondary)] hover:bg-[var(--studio-raised)] hover:text-[var(--text-primary)]"
    }`;

  return (
    <PublicLayout>
      <main className="mx-auto w-full max-w-[1680px] px-5 pt-8 pb-20 sm:px-8 md:pt-14 md:pb-28 lg:px-12 xl:px-16">
        {/* Header — title block and search share one full-width row so the
            search field isn't stranded under a narrow column of intro text. */}
        <motion.header
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mb-9 flex flex-col gap-6 md:mb-12 lg:flex-row lg:items-end lg:justify-between lg:gap-12"
        >
          <div className="min-w-0">
            <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-tertiary)] md:mb-2 md:text-xs">
              {eyebrow}
            </p>
            <h1
              className="mb-3 text-3xl font-light tracking-tight text-[var(--text-primary)] md:text-5xl"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {title}
            </h1>
            <p className="max-w-[58ch] text-sm text-[var(--text-secondary)] md:text-base">{intro}</p>
          </div>

          <div className="w-full shrink-0 lg:w-[380px] xl:w-[420px]">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-tertiary)]" />
              <input
                type="text"
                placeholder="Search the guide..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search the guide"
                className="w-full rounded-2xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)]/60 py-3 pl-11 pr-16 text-sm text-[var(--text-primary)] transition-colors placeholder:text-[var(--text-quaternary)] focus:border-[var(--accent-yellow)]/30 focus:outline-hidden"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                >
                  Clear
                </button>
              )}
            </div>
            {query && (
              <p className="ml-1 mt-2 text-xs text-[var(--text-quaternary)]">
                {filtered.length === 0
                  ? "No results found"
                  : `${filtered.reduce((sum, c) => sum + c.sections.length, 0)} results in ${filtered.length} categories`}
              </p>
            )}
          </div>
        </motion.header>

        <div className="flex gap-8 xl:gap-12">
          {/* Sticky TOC rail — desktop */}
          <motion.nav
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="sticky top-6 hidden w-[232px] shrink-0 self-start lg:block"
            aria-label="Guide topics"
          >
            <div className="flex flex-col gap-1">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-quaternary)]">
                Topics
              </p>
              <button
                onClick={() => {
                  hasInteracted.current = true;
                  setActiveId(null);
                }}
                className={tocButtonClass(activeId === null)}
              >
                All Topics
              </button>
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => selectCategory(cat.id)}
                    className={tocButtonClass(activeId === cat.id)}
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                    {cat.title}
                  </button>
                );
              })}
            </div>
          </motion.nav>

          {/* Mobile TOC toggle */}
          <div className="fixed bottom-6 right-6 z-40 lg:hidden">
            <button
              onClick={() => setMobileTocOpen(!mobileTocOpen)}
              aria-label="Browse topics"
              className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--accent-yellow)] text-[var(--text-inverse)] shadow-lg transition-transform active:scale-95"
            >
              <ChevronDown className={`h-5 w-5 transition-transform ${mobileTocOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileTocOpen && (
              <div className="absolute bottom-14 right-0 flex w-56 flex-col gap-1 rounded-2xl border border-[var(--studio-divider-strong)] bg-[var(--studio-base)]/95 p-3 shadow-2xl backdrop-blur-xl">
                <p className="mb-1 px-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-quaternary)]">
                  Topics
                </p>
                <button
                  onClick={() => {
                    hasInteracted.current = true;
                    setActiveId(null);
                    setMobileTocOpen(false);
                  }}
                  className={tocButtonClass(activeId === null)}
                >
                  All Topics
                </button>
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => selectCategory(cat.id)}
                      className={tocButtonClass(activeId === cat.id)}
                    >
                      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                      {cat.title}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Main content area */}
          <div ref={contentRef} className="min-w-0 flex-1 scroll-mt-8">
            <AnimatePresence mode="wait">
              {filtered.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="py-20 text-center"
                >
                  <Search className="mx-auto mb-4 h-10 w-10 text-[var(--text-quaternary)]" />
                  <p className="text-sm text-[var(--text-secondary)]">
                    No results for "{query}". Try a different search term.
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key={activeId || "all"}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                  className="flex flex-col gap-12 md:gap-16"
                >
                  {filtered.map((category) => {
                    const Icon = category.icon;
                    return (
                      <section key={category.id} id={category.id} className="scroll-mt-8">
                        <div className="mb-5 flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--accent-yellow-dim)]">
                            <Icon className="h-5 w-5 text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
                          </div>
                          <h2
                            className="text-xl font-light tracking-tight text-[var(--text-primary)] md:text-2xl"
                            style={{ fontFamily: "Outfit, sans-serif" }}
                          >
                            {category.title}
                          </h2>
                        </div>

                        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
                          {category.sections.map((section) => (
                            <SectionCard key={section.id} section={section} />
                          ))}
                        </div>
                      </section>
                    );
                  })}

                  {/* Bottom CTA */}
                  <div className="rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)]/50 p-6 text-center md:p-8">
                    <HelpCircle className="mx-auto mb-3 h-8 w-8 text-[var(--text-quaternary)]" />
                    <h3
                      className="mb-2 text-lg font-light text-[var(--text-primary)]"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      Still have questions?
                    </h3>
                    <p className="mb-4 text-sm text-[var(--text-secondary)]">
                      We're here to help. Reach out anytime and we'll get back to you quickly.
                    </p>
                    <a
                      href={`mailto:${supportEmail()}`}
                      className="inline-flex items-center gap-2 rounded-full bg-[var(--accent-yellow)] px-6 py-2.5 text-sm font-medium text-[var(--text-inverse)] transition-transform active:scale-95"
                    >
                      <Mail className="h-4 w-4" />
                      {supportEmail()}
                    </a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </PublicLayout>
  );
};

export default GuideView;
